import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { graphql, useFragment } from 'react-relay/hooks'
import { FormattedDate } from 'react-intl'
import { FlexRow, Icon, Table as Tables, Text, RouterLink, ProgressIndicator } from 'elements'
import { getReportName } from 'utils/report'
import ReportSources from './ReportSources'
import ReportContextMenu from './ReportContextMenu'
import ReportStatus from './ReportStatus'
import { useAuth } from 'hooks'
import ReportWatcher from '../ReportWatcher'

const { Row, Cell } = Tables.Card

export default function ReportsTableRow({ report }) {
  const history = useHistory()
  const { currentOrg } = useAuth()
  const [working, setWorking] = useState(null)

  const row = useFragment(
    graphql`
      fragment ReportsTableRow_report on Report {
        id
        rowId
        kind
        name
        status
        createdAt
        deletedAt
        company {
          id
        }
        ...ReportSources_report
        ...ReportContextMenu_report
      }
    `,
    report,
  )

  // this will be null if the row has been permanently deleted
  if (!row) {
    return null
  }

  const reportName = getReportName(row)
  const isSuccessful = row.status === 'SUCCESS'
  const isArchived = !!row.deletedAt
  const reportPath = `/orgs/${currentOrg.slug}/companies/${row.company.id}/reports/${row.id}`

  const cellProps = {
    kind: isArchived ? 'inactive' : undefined,
  }

  const rowProps = isSuccessful
    ? {
        interactive: true,
        onClick: () => history.push(reportPath),
      }
    : {}

  const contentProps = isSuccessful
    ? {
        as: RouterLink,
        to: reportPath,
      }
    : {}

  return (
    <Row {...rowProps}>
      <ReportWatcher reportId={row.id} />
      <Cell {...cellProps}>
        <FlexRow {...contentProps} alignItems="center">
          <Icon
            name={isArchived ? 'trash' : 'document-report'}
            size="medium"
            mr="xlarge"
            color="foreground.0"
          />
          <Text fontSize="large" lineHeight="large" fontWeight="bold" truncate title={reportName}>
            {reportName}
          </Text>
        </FlexRow>
      </Cell>
      <Cell {...cellProps}>
        {working ? (
          <ProgressIndicator message={working.label} progress={working.progress} width="50%" />
        ) : null}
      </Cell>
      <Cell {...cellProps}>
        <Text {...contentProps} color="neutral.0" whiteSpace="nowrap" title={row.createdAt}>
          <FormattedDate value={row.createdAt} />
        </Text>
      </Cell>
      <Cell {...cellProps}>
        <ReportSources report={row} />
      </Cell>
      <Cell {...cellProps}>
        <ReportStatus report={row} />
      </Cell>
      <Cell {...cellProps}>
        <FlexRow justifyContent="center" position="relative">
          <ReportContextMenu report={row} setWorking={setWorking} />
        </FlexRow>
      </Cell>
    </Row>
  )
}
